/**
 * Read HS256 JWT payload without verifying signature (client-side hint only).
 * Backend signs login tokens with `{ id }`; impersonation tokens also include
 * `actor_id`, `purpose: 'impersonation'`, and `jti`.
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const part = parts[1];
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(b64 + pad);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Expiry timestamp (ms) from the token's `exp` claim, or null when the token
 * carries no expiry. Sessions minted before the server added `expiresIn` have
 * no `exp` and never expire.
 */
export function getAuthTokenExpiryMs(token) {
  const p = decodeJwtPayload(token);
  const exp = p?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
  return exp * 1000;
}

/** True when the token has an `exp` claim that is already in the past. */
export function isAuthTokenExpired(token, skewMs = 0) {
  const expiryMs = getAuthTokenExpiryMs(token);
  if (expiryMs == null) return false;
  return Date.now() + skewMs >= expiryMs;
}

/** Numeric user id from token, if present (matches tradestation-server jwt.sign payload). */
export function getUserIdFromAuthToken(token) {
  const p = decodeJwtPayload(token);
  if (p == null) return undefined;
  const id = p.id;
  if (id == null) return undefined;
  const n = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(n) ? n : undefined;
}
